<template>
    <CqModal
        :showModal="show"
        :heading="title?.id ? 'Bewerk roostertitel' : 'Voeg roostertitel toe'"
        :preventClosing="saving"
        @closeModal="closeModal"
    >
        <CqTextInput v-if="title" label="Titel" v-model="title.title" @keydown.enter="save" @update:modelValue="onChangeValue" :invalid="!!error" :invalidFeedback="error" />
        <CqButtonBar>
            <CqButton dangerous @click="destroy" :disabled="saving" label="Verwijderen" v-if="title && title.id" />
            <CqButton primary @click="save" :disabled="saving || !validateValue()" label="Opslaan" icon="save" />
        </CqButtonBar>
    </CqModal>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {CqButton, CqButtonBar, CqModal, CqTextInput} from '@cquential/ui';
    import {ApiClient, Title} from '@cquential/api';
    import {RosterData} from '@/types/types';
    import GlobalErrorModal from '@/components/modals/GlobalErrorModal.vue';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqModal,
            CqTextInput,
        },
        emits: [
            'create:title',
            'update:title',
            'delete:title',
        ],
    })
    export default class TitleEditModal extends Vue {
        private static instance: TitleEditModal | null;

        @Prop() public roster?: RosterData;

        public title: Title | null = null;
        public valueIsValid: boolean = true;
        public error: string = '';
        public show: boolean = false;
        public saving: boolean = false;

        public mounted(): void {
            TitleEditModal.instance = this;
        }

        public beforeUnmount(): void {
            TitleEditModal.instance = null;
        }

        public static open(openTitle: Title): void {
            this.instance!.title = openTitle;
            this.instance!.show = true;
        }

        public onChangeValue(): void {
            this.error = '';
            this.valueIsValid = this.validateValue();
            if (!this.valueIsValid && this.title) {
                if (this.title.title.length >= 255) {
                    this.error = 'Titel is te lang';
                }
                if (this.title.title.length === 0) {
                    this.error = 'Vul een titel in';
                }
            }
        }

        public validateValue(): boolean {
            return !!this.title
                && this.title.title.length < 255
                && this.title.title.length > 0;
        }

        public async destroy(): Promise<void> {
            if (this.saving || !this.roster || !this.title?.id) {
                return;
            }

            this.saving = true;
            try {
                await ApiClient.instance.deleteTitle(this.title.id, this.roster.rosterRankSet.id!, this.roster.rosterId!);
                this.$emit('delete:title', this.title!);
                this.closeModal();
            } catch {
                this.valueIsValid = false;
                this.error = 'Kon de roostertitel niet verwijderen. Probeer het opnieuw.';
            } finally {
                this.saving = false;
            }
        }

        public async save(): Promise<void> {
            if (this.saving || !this.roster || !this.title || !this.validateValue()) {
                return;
            }

            this.saving = true;
            try {
                const titleId: number = await ApiClient.instance.saveTitle(this.title, this.roster.rosterRankSet.id!, this.roster.rosterId!);
                if (this.title.id !== titleId) {
                    this.title.id = titleId;
                    try {
                        // GET the Title, to get the correct rank
                        this.title = await ApiClient.instance.fetchTitle(this.title.id, this.roster.rosterRankSet.id!, this.roster.rosterId!);
                        this.$emit('create:title', this.title);
                        this.closeModal();
                    } catch (error: unknown) {
                        /** @see GlobalErrorModal.globalErrorHandler() */
                        // @ts-expect-error TS2339
                        GlobalErrorModal.__vfdConstructor.globalErrorHandler(error);
                    }
                } else {
                    this.$emit('update:title', this.title!);
                    this.closeModal();
                }
            } catch {
                this.valueIsValid = false;
                this.error = 'Kon de wijziging niet opslaan. Probeer het opnieuw.';
            } finally {
                this.saving = false;
            }
        }

        public closeModal(): void {
            this.show = false;
        }
    }
</script>
